<style lang="scss">
  :global(.cpnav svg:focus) {
    outline: 0;
  }
</style>

<script lang="ts">
  import { get } from 'svelte/store'
  import { getIconAttrs } from '@lib/images/images'

  import PassportIcon from '@nav/images/passport.svg'
  import { NavContainer } from '@packages/ui'

  import { AUTH_PROVIDERS, NavDirections, Themes } from '@packages/utils/enums'
  import type { TEvmContract, TNavConfig } from '@packages/utils/types'
  import { isLoggedInStore, fn, clientPublicStore } from '@packages/stores'
  import { ethers } from '@credenza3/core-web-evm-ext'
  import { onDestroy } from 'svelte'

  import { on, PassportEvents } from '@lib/events/events'
  import { analytics } from '@lib/mixpanel/mixpanel'
  import { getAddress } from '@src/passport/blockchain'
  import { configStore, providerStore, cloakStore } from '@src/stores'
  import ExpandedNav from './components/ExpandedNav.svelte'
  import { getAvailableLoginMethods } from '@pages/login/authMethods.helper'
  import { resolveNavbarOverrideImage } from '@packages/utils/lib/navbarOverride'

  export let styles: { [key: string]: string }

  export let navConfig: TNavConfig

  const config = get(configStore)
  const { openUI, getCREDContract, getBalance: getEvmBalance, login } = get(fn)

  let balance: string = ''

  const getBalance = async () => {
    try {
      const userAddress = await getAddress()
      if (!userAddress) return

      const { decimals, contract, address: contractAddress } = (await getCREDContract()) as TEvmContract
      const weiBalance = await getEvmBalance()
      const balanceResult = ethers.formatUnits(weiBalance, decimals)
      const symbol = await contract.symbol()
      balance = `(${balanceResult} ${$clientPublicStore?.ui_settings.cred_alias || 'cred'})`
      analytics.track('cp_nav_balance_read', {
        chain: $configStore.chainId,
        contract: contractAddress,
        decimals,
        balance: balanceResult,
        symbol,
      })
    } catch (e) {
      console.log(e)
      balance = ''
    }
  }

  const offRecheckBalanceListener = on(PassportEvents.RECHECK_BALANCE, async (txHashes: string[]) => {
    if (txHashes?.length) await Promise.all(txHashes.map((hash) => get(providerStore)?.waitForTransaction(hash)))
    await getBalance()
  })

  const offUserAddressListener = on(PassportEvents.LOGIN, async () => {
    await getBalance()
  })

  const onProviderChangeListener = providerStore.subscribe(async (provider) => {
    if (provider) {
      await getBalance()
    }
  })

  $: navbarOverrideImage = resolveNavbarOverrideImage(
    $clientPublicStore?.ui_settings,
    $isLoggedInStore,
  )

  $: uiStyleEntries = Object.entries($clientPublicStore?.ui_settings || {})
    .filter(([key]) => !key.endsWith('_url'))
    .map(([key, value]) => {
      if (key === 'text_color') {
        return ['color', value]
      }
      return [`${key.replace(/_/g, '-')}`, value]
    })

  $: combined = {
    ...styles,
    ...Object.fromEntries(uiStyleEntries),
  }

  $: cssVarStyles = Object.entries(combined)
    .map(([key, value]) => `${key}:${value} !important`)
    .join(';')

  let isExpanded: boolean = false
  let isMinimized: boolean = navConfig.minimization?.enabled || false
  const navDirection = config.nav?.direction || NavDirections.TOP
  const theme = config.nav?.theme || Themes.WHITE

  let chevronStyles = ''
  $: {
    chevronStyles = Object.entries({
      // safari doesn't respect transform as a direct attribute
      ...(isExpanded ? { transform: 'scale(1, -1)' } : {}),
      ...getIconAttrs(theme),
    })
      .map(([key, value]) => `${key}:${value}`)
      .join(';')
  }

  const iconStyles = Object.entries(getIconAttrs(theme))
    .map(([key, value]) => `${key}:${value}`)
    .join(';')

  const loggedInStoreUnSub = isLoggedInStore.subscribe((isLoggedIn) => {
    if (!isLoggedIn) isExpanded = false
  })

  onDestroy(() => {
    offRecheckBalanceListener()
    offUserAddressListener()
    loggedInStoreUnSub()
    onProviderChangeListener()
  })

  const onClickHadle = async () => {
    const loginMethods = getAvailableLoginMethods()
    if (!$cloakStore && loginMethods.length === 1) {
      await login(AUTH_PROVIDERS.OAUTH)
    } else {
      openUI()
    }
  }
</script>

<NavContainer
  options={{
    isExpanded,
    theme,
    isMinimized,
    cssVarStyles,
    navConfig,
    navDirection,
    iconStyles,
    onItemClicked: (page) => openUI(page),
    expandedNavComponent: ExpandedNav,
    navbarOverrideImage,
  }}
  navItemOptions={{
    onClick: () => ($isLoggedInStore ? (isExpanded = !isExpanded) : onClickHadle()),
    title: `${$clientPublicStore?.name}`,
    passIcon: $clientPublicStore?.ui_settings.logo_url || PassportIcon,
    chevronStyles,
  }}
  balanceOptions={{
    isShown: config.buyTokens !== false,
    balance,
  }}
></NavContainer>
